import React, { useCallback, useRef, useState, useEffect, useMemo } from 'react'
import { Input, InputRef, Tag, Tooltip, notification } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import MultiSelectTag from './MultiSelectTag'
import { IEditTag, retrieveArticlesTags } from 'Services/Kiosk/TagsService'
import { useAppDispatch, useAppSelector } from 'hooks'
import { setEditContainerTags } from 'Slices/Kiosk/TableOptionsSlice'
import { filterGeneralDistinct, renderTranslation } from 'Helpers/GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'

interface ArticleTagContainerProps {
  populateData: boolean
}

function ArticleTagContainer(props: ArticleTagContainerProps) {
  const dispatch = useAppDispatch()
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const tableFiltersTags = useAppSelector(state => state.present.tableOptions.tableFiltersTags)
  const language = useAppSelector(state => state.present.language.value)
  // const fullEditSelection = useAppSelector(state => state.present.tableOptions.fullEditSelection)
  const editContainerTags = useAppSelector(state => state.present.tableOptions.editContainerTags) as IEditTag[]

  const [inputVisible, setInputVisible] = useState(false)
  const [editInputIndex, setEditInputIndex] = useState(-1)
  const [editInputValue, setEditInputValue] = useState('')
  const editInputRef = useRef<InputRef>(null)
  const maxTagCharVisible = 20

  useEffect(() => {
    editInputRef.current?.focus()
  }, [editInputValue])

  useEffect(() => {
    //for a single article load all tags associated
    if (props.populateData && rowKeysSelected.length == 1) {
      retrieveArticlesTags().then(data => {
        const tags = data && Array.isArray(data) ? data : []
        dispatch(setEditContainerTags(tags))
      })
    }

    //if there is a filter on tag with an only value show only that tag
    if (props.populateData && tableFiltersTags.length == 1) {
      dispatch(setEditContainerTags(tableFiltersTags))
    }
  }, [props.populateData])

  const handleClose = useCallback(
    (removedTagId: number) => {
      let newTags = [...editContainerTags]
      const deletedTag = newTags.find((x: IEditTag) => {
        return x?.id === removedTagId
      })
      if (deletedTag && deletedTag?.id > 0) {
        if (!deletedTag.isAdded) {
          const deletedTagIndex = newTags.findIndex((x: IEditTag) => {
            return x?.id === removedTagId
          })
          var newObject = Object.assign({}, deletedTag)
          newObject.softDeleted = true
          newTags.splice(deletedTagIndex, 1, newObject)
        } else {
          newTags = newTags.filter(x => x.id !== removedTagId)
        }
      } else {
        newTags = newTags.filter(x => x.id !== removedTagId)
      }

      dispatch(setEditContainerTags(newTags))
    },
    [editContainerTags],
  )

  const showInput = () => {
    setInputVisible(true)
  }

  const handleInputConfirm = (tagsSelected: IEditTag[]) => {
    if (tagsSelected && tagsSelected.length > 0) {
      const tags = [...editContainerTags]
      const alreadyInsertTag = []

      tagsSelected.forEach((selected: IEditTag) => {
        const tag = tags.find((tag: IEditTag) => selected?.id === tag?.id || selected?.slug === tag?.slug)
        if (!tag) {
          const selectedTag = { ...selected, isAdded: !tag } as IEditTag
          tags.push(selectedTag)
        } else {
          if (selected?.id === tag?.id || selected?.slug === tag?.slug) {
            alreadyInsertTag.push(selected?.slug)
          }
        }
      })

      const distinctInsertedTags = alreadyInsertTag.filter(filterGeneralDistinct)

      if (distinctInsertedTags.length > 0) {
        notification.open({
          description: `${renderTranslation(language, TranslationKeys.tagsAlreadyInsert)}: ${distinctInsertedTags.join(
            ', ',
          )}`,
          placement: 'topLeft',
          message: '',
        })
      }

      dispatch(setEditContainerTags(tags))
    }
    setInputVisible(false)
  }

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditInputValue(e.target.value)
  }

  const handleEditInputConfirm = () => {
    if (editInputValue) {
      if (editInputValue.indexOf('|') !== -1 || editInputValue.split('_').length > 2) {
        notification.open({
          description: renderTranslation(language, TranslationKeys.tagsValidation),
          placement: 'topLeft',
          message: '',
        })

        return
      }

      const newTags = [...editContainerTags]

      if (newTags[editInputIndex]?.softDeleted) {
        setEditInputIndex(-1)
        setEditInputValue('')
        return
      }

      if (newTags[editInputIndex]?.slug === editInputValue) {
        setEditInputIndex(-1)
        setEditInputValue('')
        return
      }

      newTags[editInputIndex] = {
        ...newTags[editInputIndex],
        slug: editInputValue,
        isEdited: newTags[editInputIndex]?.id > 0 && !newTags[editInputIndex]?.isAdded,
      } as IEditTag
      dispatch(setEditContainerTags(newTags))
      setEditInputIndex(-1)
      setEditInputValue('')
    }
  }

  const tags = useMemo(() => {
    return editContainerTags.map<React.ReactNode>((tag, index) => {
      if (editInputIndex === index) {
        return (
          <Input
            ref={editInputRef}
            key={'input-' + tag?.id}
            size='small'
            value={editInputValue}
            className={'edit-input'}
            onChange={handleEditInputChange}
            onBlur={handleEditInputConfirm}
            onPressEnter={handleEditInputConfirm}
          />
        )
      }
      const isLongTag = tag?.slug?.length > 20
      const tagElem = (
        <Tag
          key={tag?.id}
          style={{ userSelect: 'none' }}
          className={`article-tag ${
            tag?.softDeleted
              ? 'deleted'
              : (tag?.isEdited ? 'edited' : '') + (tag?.id < 0 || tag?.isAdded ? ' added' : '')
          }`}
        >
          <span
            onClick={e => {
              if (!tag?.softDeleted) {
                setEditInputIndex(index)
                setEditInputValue(tag?.slug)
                e.preventDefault()
              }
            }}
          >
            {isLongTag ? `${tag?.slug.slice(0, maxTagCharVisible)}...` : tag?.slug}
          </span>
          {!tag?.softDeleted ? (
            <span>
              <CloseOutlined
                className='close'
                onClick={e => {
                  handleClose(tag?.id)
                }}
              />
            </span>
          ) : null}
        </Tag>
      )

      return isLongTag ? (
        <Tooltip title={tag?.slug} key={'pop-' + tag?.id}>
          {tagElem}
        </Tooltip>
      ) : (
        tagElem
      )
    })
  }, [rowKeysSelected, editContainerTags, editInputValue, editInputRef])

  return (
    <div className='tags-box'>
      {tags}
      {inputVisible ? (
        <MultiSelectTag tags={editContainerTags} handleSave={handleInputConfirm} />
      ) : (
        <Tag className='article-tag' icon={<PlusOutlined />} onClick={showInput}>
          {renderTranslation(language, TranslationKeys.editModalTagAddButtonText)}
        </Tag>
      )}
    </div>
  )
}

export default ArticleTagContainer
